import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { getErrorMessage } from '../utils/errorHandler';
import { toast } from 'sonner';
import { Plus, Shield, UserX, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import LogoLoader from '@/components/LogoLoader';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { formatDisplayDateTime } from '@/utils/date';

const Users = () => {
  const { token, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    confirm_password: '',
    role: 'ReadOnly'
  });

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'Admin') {
      toast.error('Access denied. Admin role required.');
      setLoading(false);
      return;
    }
    loadUsers();
  }, [user]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await api.get('/users', token);
      setUsers(data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      const payload = {
        email: formData.email,
        full_name: formData.full_name,
        password: formData.password,
        role: formData.role
      };
      
      await api.post('/auth/register', payload, token);
      toast.success(`User ${formData.email} created successfully`);
      setShowCreateDialog(false);
      loadUsers();
      setFormData({
        email: '',
        full_name: '',
        password: '',
        confirm_password: '',
        role: 'ReadOnly'
      });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create user'));
    }
  };

  const handleUpdateRole = async () => {
    try {
      await api.patch(`/users/${selectedUser.id}/role`, { role: selectedUser.role }, token);
      toast.success('User role updated successfully');
      setShowRoleDialog(false);
      loadUsers();
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const handleToggleActive = async () => {
    try {
      await api.patch(`/users/${selectedUser.id}/toggle-active`, {}, token);
      toast.success(`User ${selectedUser.is_active ? 'deactivated' : 'activated'} successfully`);
      setShowDeactivateDialog(false);
      loadUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'Admin': 'bg-red-100 text-red-800',
      'Operations Manager': 'bg-blue-100 text-blue-800',
      'Finance': 'bg-purple-100 text-purple-800',
      'HR': 'bg-orange-100 text-orange-800',
      'ReadOnly': 'bg-gray-100 text-gray-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <LogoLoader message="Loading users..." />;
  }

  if (user?.role !== 'Admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="pt-6 text-center">
            <Shield className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">You need Admin privileges to access User Management.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-1">Manage system users and their access levels</p>
        </div>
        <Button 
          onClick={() => setShowCreateDialog(true)} 
          className="bg-utis-forest hover:bg-utis-forest/90 text-white" 
          data-testid="create-user-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create User
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Users ({users.length})</CardTitle>
          <CardDescription>View and manage user accounts</CardDescription>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No users found. Click "Create User" to add the first user.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((usr) => (
                  <TableRow key={usr.id}>
                    <TableCell className="font-medium">{usr.full_name}</TableCell>
                    <TableCell>{usr.email}</TableCell>
                    <TableCell>
                      <Badge className={getRoleBadgeColor(usr.role)}>
                        {usr.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {usr.is_active ? (
                        <Badge className="bg-utis-sage/20 text-utis-forest">
                          <UserCheck className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="destructive">
                          <UserX className="w-3 h-3 mr-1" />
                          Inactive
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {formatDisplayDateTime(usr.created_at, '-')}
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedUser(usr);
                            setShowRoleDialog(true);
                          }}
                          className="text-xs"
                          data-testid={`edit-role-${usr.email}`}
                        >
                          Change Role
                        </Button>
                        <Button
                          size="sm"
                          variant={usr.is_active ? "destructive" : "default"}
                          onClick={() => {
                            setSelectedUser(usr);
                            setShowDeactivateDialog(true);
                          }}
                          className="text-xs"
                          data-testid={`toggle-active-${usr.email}`}
                        >
                          {usr.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>Add a new user to the system with appropriate access level</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                required
                placeholder="John Doe"
                data-testid="user-full-name"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                placeholder="john.doe@utis.com"
                data-testid="user-email"
              />
            </div>
            <div>
              <Label>Role *</Label>
              <Select value={formData.role} onValueChange={(val) => setFormData({...formData, role: val})}>
                <SelectTrigger data-testid="user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Admin">Admin - Full access</SelectItem>
                  <SelectItem value="Operations Manager">Operations Manager - Projects, POs, approvals</SelectItem>
                  <SelectItem value="Finance">Finance - Invoices, receipts, AR/AP</SelectItem>
                  <SelectItem value="HR">HR - Consultants, commissions</SelectItem>
                  <SelectItem value="ReadOnly">ReadOnly - View only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Password *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                placeholder="Min 8 characters"
                minLength={8}
                data-testid="user-password"
              />
            </div>
            <div>
              <Label>Confirm Password *</Label>
              <Input
                type="password"
                value={formData.confirm_password}
                onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                required
                placeholder="Re-enter password"
                data-testid="user-confirm-password"
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
              <strong>Note:</strong> The user will be able to login immediately with these credentials.
            </div>
            <Button type="submit" className="w-full bg-utis-forest hover:bg-utis-forest/90 text-white" data-testid="submit-create-user">
              Create User
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>Update the role for {selectedUser?.full_name}</DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div>
                <Label>New Role</Label>
                <Select 
                  value={selectedUser.role} 
                  onValueChange={(val) => setSelectedUser({...selectedUser, role: val})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Admin">Admin</SelectItem>
                    <SelectItem value="Operations Manager">Operations Manager</SelectItem>
                    <SelectItem value="Finance">Finance</SelectItem>
                    <SelectItem value="HR">HR</SelectItem>
                    <SelectItem value="ReadOnly">ReadOnly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleUpdateRole} className="w-full bg-utis-forest hover:bg-utis-forest/90 text-white">
                Update Role
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Deactivate/Activate Confirmation Dialog */}
      <AlertDialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {selectedUser?.is_active ? 'Deactivate User' : 'Activate User'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedUser?.is_active ? (
                <>
                  Are you sure you want to deactivate <strong>{selectedUser?.full_name}</strong>? 
                  They will not be able to login until reactivated.
                </>
              ) : (
                <>
                  Are you sure you want to activate <strong>{selectedUser?.full_name}</strong>? 
                  They will be able to login immediately.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleToggleActive}
              className={selectedUser?.is_active ? "bg-red-600 hover:bg-red-700" : "bg-utis-forest hover:bg-utis-forest/90 text-white"}
            >
              {selectedUser?.is_active ? 'Deactivate' : 'Activate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Users;
